import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Demo mode configuration
const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
const DEMO_USER = {
  username: 'demo-user',
  email: 'demo@hollandandknight.local',
  sub: 'demo-user-sub-001',
  given_name: 'Demo',
  family_name: 'User',
  accessToken: 'demo-access-token',
  refreshToken: 'demo-refresh-token',
  tokenExpiry: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
  sessionId: 'demo-session-id'
};

interface User {
  username: string;
  email: string;
  sub: string;
  given_name?: string;
  family_name?: string;
  accessToken: string;
  refreshToken?: string;
  tokenExpiry?: number;
  sessionId?: string;
  azureAccessToken?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  sessionStatus: 'active' | 'inactive' | 'expired' | 'unknown';
  signInWithAzure: () => void;
  signInWithCognito: () => void;
  signOut: () => void;
  getAccessToken: () => Promise<string | null>;
  refreshToken: () => Promise<boolean>;
  extendSession: () => Promise<boolean>;
  getCurrentSession: () => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionStatus, setSessionStatus] = useState<'active' | 'inactive' | 'expired' | 'unknown'>('unknown');
  const navigate = useNavigate();
  const location = useLocation();

  // Determine SSO mode from environment
  const isAzureEnabled = process.env.REACT_APP_AZURE_SSO_ENABLED === 'true';
  const azureTenantId = process.env.REACT_APP_AZURE_TENANT_ID;
  const azureClientId = process.env.REACT_APP_AZURE_CLIENT_ID;
  const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
  const cognitoClientId = process.env.REACT_APP_CLIENT_ID;
  const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';

  useEffect(() => {
    // Demo mode: immediately set mock user and skip all auth
    if (isDemoMode) {
      console.log('Demo mode enabled - using mock user');
      setUser(DEMO_USER);
      setSessionStatus('active');
      setLoading(false);
      return;
    }

    console.log('AuthProvider initialized with:', {
      isAzureEnabled,
      azureTenantId: azureTenantId ? 'configured' : 'missing',
      azureClientId: azureClientId ? 'configured' : 'missing',
      cognitoDomain,
      cognitoClientId: cognitoClientId ? 'configured' : 'missing'
    });

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (code) {
      // Determine callback type based on referrer or session storage
      const authMethod = sessionStorage.getItem('authMethod');
      if (authMethod === 'cognito') {
        handleCognitoCallback(code);
      } else if (authMethod === 'azure' || isAzureEnabled) {
        handleAzureCallback(code);
      } else {
        handleCognitoCallback(code);
      }
    } else {
      // Check for existing session
      checkExistingSession();
    }
  }, [location.pathname]);

  const checkExistingSession = () => {
    try {
      const existingToken = localStorage.getItem('accessToken');
      const existingUser = localStorage.getItem('userData');
      
      if (existingToken && existingUser) {
        console.log('Found existing session, checking validity');
        
        if (isTokenExpired(existingToken)) {
          console.log('Stored token is expired, clearing session');
          clearSession('expired');
          redirectToAuth();
        } else {
          console.log('Token is valid, restoring session');
          const userData = JSON.parse(existingUser);
          setUser({
            ...userData,
            accessToken: existingToken
          });
          setSessionStatus('active');
        }
      } else {
        console.log('No existing session found');
        redirectToAuth();
      }
    } catch (error) {
      console.error('Error checking existing session:', error);
      clearSession();
    } finally {
      setLoading(false);
    }
  };

  const handleAzureCallback = async (code: string) => {
    try {
      console.log('Processing Azure SSO callback');
      const apiBaseUrl = process.env.REACT_APP_API_URL;
      const redirectUri = window.location.origin + '/auth/callback';
      const environment = process.env.REACT_APP_ENVIRONMENT || 'dev';
      
      const response = await fetch(`${apiBaseUrl}/api/auth/azure/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, redirectUri, environment })
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log('Azure SSO authentication successful');
        
        const tokenExpiry = Date.now() + (data.expiresIn * 1000);
        const userData = {
          // Map Azure response fields to frontend User interface
          username: data.user.userId,                 // Azure uses userId
          email: data.user.email,
          sub: data.user.userId,                      // Use userId as sub
          given_name: data.user.firstName,            // Azure uses firstName
          family_name: data.user.lastName,            // Azure uses lastName
          accessToken: data.sessionToken || data.session?.sessionId,  // Use session token for API calls
          refreshToken: data.refreshToken,
          tokenExpiry,
          sessionId: data.sessionToken || data.session?.sessionId,  // Azure uses sessionToken
          azureAccessToken: data.accessToken
        };
        
        // Store session token (UUID) for API authentication, not Azure access token
        localStorage.setItem('accessToken', data.sessionToken || data.session?.sessionId);
        localStorage.setItem('azureAccessToken', data.accessToken);  // Keep Azure token for Graph API if needed
        localStorage.setItem('refreshToken', data.refreshToken || '');
        localStorage.setItem('tokenExpiry', tokenExpiry.toString());
        localStorage.setItem('userData', JSON.stringify(userData));
        setUser(userData);
        setSessionStatus('active');
        
        // Clear auth method and navigate to return URL or homepage
        sessionStorage.removeItem('authMethod');
        const returnUrl = localStorage.getItem('returnUrl');
        localStorage.removeItem('returnUrl');
        navigate(returnUrl || '/', { replace: true });
      } else {
        console.error('Azure SSO authentication failed:', data);
        throw new Error(data.error || 'Authentication failed');
      }
    } catch (error) {
      console.error('Azure SSO callback error:', error);
      clearSession();
      navigate('/', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const handleCognitoCallback = async (code: string) => {
    try {
      console.log('Processing Cognito callback');
      const apiBaseUrl = process.env.REACT_APP_API_URL;
      const redirectUri = `${window.location.origin}/auth/callback`;
      
      const response = await fetch(`${apiBaseUrl}/api/auth/cognito/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, redirectUri })
      });
      
      const response_data = await response.json();
      
      if (response_data.success) {
        console.log('Cognito authentication successful');
        
        const data = response_data.data; // Extract the nested data object
        const tokenExpiry = Date.now() + (data.expiresIn * 1000);
        const userData = {
          username: data.user.username,
          email: data.user.email,
          sub: data.user.sub,
          given_name: data.user.given_name,
          family_name: data.user.family_name,
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          tokenExpiry,
          sessionId: data.sessionId
        };
        
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken || '');
        localStorage.setItem('tokenExpiry', tokenExpiry.toString());
        localStorage.setItem('userData', JSON.stringify(userData));
        setUser(userData);
        setSessionStatus('active');
        
        // Clear auth method and code from URL
        sessionStorage.removeItem('authMethod');
        window.history.replaceState({}, document.title, window.location.pathname);

        const returnUrl = localStorage.getItem('returnUrl');
        localStorage.removeItem('returnUrl');
        navigate(returnUrl || '/', { replace: true });
      } else {
        const errorMessage = response_data.error || response_data.message || 'Authentication failed';
        const detailsMessage = Array.isArray(response_data.details)
          ? response_data.details.map((detail: any) => detail.message).filter(Boolean).join(' ')
          : response_data.details?.message || '';

        console.error('Cognito authentication failed:', response_data);
        throw new Error(detailsMessage || errorMessage);
      }
    } catch (error) {
      console.error('Cognito callback error:', error);
      redirectToAuth();
    } finally {
      setLoading(false);
    }
  };

  const redirectToAuth = () => {
    // Don't redirect if we're already on the callback page, login page, or if user is already authenticated
    if (location.pathname === '/auth/callback' || location.pathname === '/login' || user) {
      return;
    }

    // Store current location to return to after login
    localStorage.setItem('returnUrl', window.location.pathname + window.location.search);

    // Always redirect to login page to allow user choice
    console.log('Authentication required - redirecting to login page');
    navigate('/login');
  };

  const redirectToAzure = () => {
    sessionStorage.setItem('authMethod', 'azure');
    const redirectUri = window.location.origin + '/auth/callback';
    const authUrl = `https://login.microsoftonline.com/${azureTenantId}/oauth2/v2.0/authorize?` +
      `client_id=${azureClientId}&` +
      `response_type=code&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `scope=openid profile email&` +
      `response_mode=query`;
    
    console.log('Redirecting to Azure AD:', authUrl);
    window.location.href = authUrl;
  };

  const redirectToCognito = () => {
    sessionStorage.setItem('authMethod', 'cognito');
    const redirectUri = `${window.location.origin}/auth/callback`;
    const useCognitoFederatedAzure = process.env.REACT_APP_USE_COGNITO_FEDERATED_AZURE === 'true';
    
    let authUrl = `https://${cognitoDomain}.auth.${awsRegion}.amazoncognito.com/oauth2/authorize?` +
      `client_id=${cognitoClientId}&` +
      `response_type=code&` +
      `scope=email+openid+profile&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}`;
    
    // If using Cognito-federated Azure SSO, add identity_provider to skip Cognito hosted UI
    if (useCognitoFederatedAzure) {
      authUrl += `&identity_provider=AzureAD`;
    }
    
    console.log('Redirecting to Cognito:', authUrl);
    window.location.href = authUrl;
  };

  const signInWithAzure = () => {
    // Check if we should use Cognito-federated Azure instead of direct Azure
    const useCognitoFederatedAzure = process.env.REACT_APP_USE_COGNITO_FEDERATED_AZURE === 'true';
    if (useCognitoFederatedAzure) {
      redirectToCognito();
    } else {
      redirectToAzure();
    }
  };

  const signInWithCognito = () => {
    redirectToCognito();
  };

  const signOut = async () => {
    console.log('Signing out - clearing local session');
    
    clearSession();
    setUser(null);
    
    // SSO session is managed by the organization/browser
    // Just clear local session and redirect to login
    navigate('/login');
  };

  const clearSession = (status: 'inactive' | 'expired' = 'inactive') => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('userData');
    localStorage.removeItem('returnUrl');
    localStorage.removeItem('azureAccessToken');
    setSessionStatus(status);
  };

  const getStoredTokenExpiry = (): number | null => {
    if (user?.tokenExpiry) {
      return user.tokenExpiry;
    }
    const storedExpiry = localStorage.getItem('tokenExpiry');
    return storedExpiry ? parseInt(storedExpiry, 10) : null;
  };

  const parseJwtExpiry = (token: string): number | null => {
    if (!token || !token.includes('.')) {
      return null;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload?.exp ? payload.exp * 1000 : null;
    } catch {
      return null;
    }
  };

  const isTokenExpired = (token: string): boolean => {
    const expiryTime = parseJwtExpiry(token) ?? getStoredTokenExpiry();
    if (!expiryTime) {
      return false;
    }

    return Date.now() >= expiryTime;
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const storedRefreshToken = localStorage.getItem('refreshToken');
      if (!storedRefreshToken) {
        console.log('No refresh token available');
        return false;
      }

      const apiBaseUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiBaseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: storedRefreshToken })
      });

      const data = await response.json();

      if (data.success && data.accessToken) {
        console.log('Token refreshed successfully');
        
        // Update stored tokens
        localStorage.setItem('accessToken', data.accessToken);
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }

        // Calculate expiry time
        const tokenExpiry = Date.now() + (data.expiresIn * 1000);
        localStorage.setItem('tokenExpiry', tokenExpiry.toString());

        // Update user object
        if (user) {
          setUser({
            ...user,
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            tokenExpiry
          });
        }

        setSessionStatus('active');

        return true;
      } else {
        console.error('Token refresh failed:', data);
        return false;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    let token = user?.accessToken || localStorage.getItem('accessToken');
    
    if (!token) {
      console.log('No access token available');
      return null;
    }

    // Check if token is expired or will expire soon (within 5 minutes)
    if (isTokenExpired(token) || isTokenExpiringSoon(token, 5 * 60)) {
      if (token.includes('.')) {
        console.log('JWT token expired or expiring soon, attempting refresh');
        const refreshSuccess = await refreshToken();
        
        if (refreshSuccess) {
          token = localStorage.getItem('accessToken');
        } else {
          console.log('Token refresh failed, signing out');
          await signOut();
          return null;
        }
      } else {
        console.log('Session token expired or expiring soon, requesting session extension');
        const extended = await extendSession();
        if (!extended) {
          console.log('Session extension failed, signing out');
          await signOut();
          return null;
        }
        token = localStorage.getItem('accessToken');
      }
    }
    
    return token;
  };

  const getCurrentSession = async (): Promise<any> => {
    try {
      const token = user?.accessToken || localStorage.getItem('accessToken');
      if (!token) return null;

      const apiBaseUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiBaseUrl}/api/sessions/current`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const sessionData = await response.json();
        setSessionStatus('active');
        return sessionData;
      } else if (response.status === 401) {
        setSessionStatus('expired');
        return null;
      } else {
        setSessionStatus('inactive');
        return null;
      }
    } catch (error) {
      console.error('Error getting current session:', error);
      setSessionStatus('unknown');
      return null;
    }
  };

  const extendSession = async (): Promise<boolean> => {
    try {
      const token = user?.accessToken || localStorage.getItem('accessToken');
      if (!token) return false;

      // JWT tokens can't be extended server-side — refresh via token endpoint instead
      if (token.includes('.') && token.split('.').length === 3) {
        console.log('JWT token detected, refreshing via token endpoint instead of session extension');
        return await refreshToken();
      }

      const apiBaseUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiBaseUrl}/api/sessions/extend`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Session extended successfully:', data);
        if (data?.expiresAt) {
          const newExpiry = new Date(data.expiresAt).getTime();
          localStorage.setItem('tokenExpiry', newExpiry.toString());
          setUser(prev => prev ? { ...prev, tokenExpiry: newExpiry } : prev);
        }
        setSessionStatus('active');
        return true;
      } else {
        console.error('Failed to extend session');
        return false;
      }
    } catch (error) {
      console.error('Error extending session:', error);
      return false;
    }
  };

  const isTokenExpiringSoon = (token: string, secondsThreshold: number): boolean => {
    const expiryTime = parseJwtExpiry(token) ?? getStoredTokenExpiry();
    if (!expiryTime) {
      return false;
    }

    return Date.now() >= (expiryTime - secondsThreshold * 1000);
  };

  const value = {
    user,
    loading,
    sessionStatus,
    signInWithAzure,
    signInWithCognito,
    signOut,
    getAccessToken,
    refreshToken,
    extendSession,
    getCurrentSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};